import { useState, type ReactNode } from "react"
import { FlightOption } from "@/types/flights/flight-option"
import { cn } from "cn"

import { flightOptionToAmplitudeProperties } from "@/lib/analytics/amplitude-properties"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import type { FlightQuoteDisplayOption } from "@/lib/flight-booking-utils"
import { flightOptionToBadges } from "@/lib/flight-utils"
import { useFlightCardHover } from "@/lib/hooks/use-flight-card-hover"
import { selectFlightCheckoutEnabled } from "@/lib/storage/app-slice"
import { selectDirectToAirline } from "@/lib/storage/flight-search-slice"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { setHoveredFlight } from "@/lib/storage/map-hover-slice"
import { FlightOptionBadges } from "@/components/flight-option-badges"
import { FlightOptionPrice } from "@/components/flight-option-price"
import IconCheck from "@/components/icons/check"
import IconChevronDown from "@/components/icons/chevron-down"
import IconCross from "@/components/icons/cross"
import IconGoTo from "@/components/icons/go-to"
import IconInfo from "@/components/icons/info"
import { FlightSliceCardHeader } from "./flight-slice-header"
import { AriaButton } from "./ui/aria-button"
import { Button } from "./ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible"
import { FlightLegsDetail } from "./ui/flight-legs-detail"
import { IconButton } from "./ui/icon-button"
import { Separator } from "./ui/separator"

type BaseFlightOptionCardProps = {
  defaultExpanded?: boolean
  alwaysExpanded?: boolean
  isReturn?: boolean
  sliceLabel?: string
  className?: string
  header?: ReactNode
  footer?: ReactNode
}

type FlightOptionCardProps =
  | (BaseFlightOptionCardProps & {
      flightOption: FlightOption | FlightQuoteDisplayOption
      type: "checkout" | "booking" | "message"
      onSelect?: undefined
    })
  | (BaseFlightOptionCardProps & {
      flightOption: FlightOption
      type?: "details" | "selected"
      onSelect: (flightOption: FlightOption) => void
    })

export function FlightOptionCard({
  flightOption,
  defaultExpanded,
  alwaysExpanded,
  type = "details",
  isReturn,
  sliceLabel,
  onSelect,
  header,
  footer,
  className,
}: FlightOptionCardProps) {
  const dispatch = useAppDispatch()
  const [expanded, _setExpanded] = useState<boolean>(
    alwaysExpanded === true ? alwaysExpanded : (defaultExpanded ?? false)
  )
  const flightCheckoutEnabled = useAppSelector(selectFlightCheckoutEnabled)
  const directToAirline = selectDirectToAirline(
    flightOption.vendor,
    flightOption.bookable
  )

  const sliceTicketing = flightOption.slice_ticketing

  const hasCancellationPolicy = !!sliceTicketing?.cancellation_hours

  function setExpanded(open: boolean) {
    if (!alwaysExpanded) {
      _setExpanded(open)
    }
  }

  function cancellationPolicy() {
    if (!hasCancellationPolicy) {
      return null
    }

    return (
      <div className="flex items-center gap-1 text-xs text-muted">
        {sliceTicketing.cancellation_hours ? (
          <>
            <IconCheck className="text-foreground" strokeWidth={2.5} />

            <span>
              Free cancellation within {sliceTicketing.cancellation_hours} hours
              of booking
            </span>
          </>
        ) : (
          <>
            <IconCross className="text-red-10" strokeWidth={2.5} />
            <span>Not cancellable</span>
          </>
        )}
        {/* todo: see if there is baggage data we can use */}
        {/* <span>·</span>
              <span>Carry-on bag included [FAKE]</span> */}
      </div>
    )
  }

  const isDetailsType = type === "details"
  const isCheckoutType = type === "checkout"
  const isBookingType = type === "booking"
  const isMessageType = type === "message"
  const currentSlice = flightOption.flight_slice

  const hoverProps = useFlightCardHover({
    slice: currentSlice,
    enabled: !isCheckoutType,
  })

  function notes() {
    const legNotes = flightOption.flight_slice.flight_legs.flatMap(
      (leg) => leg.properties.notes ?? []
    )

    return (
      flightOption.flight_slice.properties.notes?.filter(
        (note) =>
          note.type !== "connection" &&
          note.type !== "redeye" &&
          !legNotes.some(
            (legNote) =>
              legNote.type === note.type && legNote.value === note.value
          )
      ) ?? []
    )
  }

  function selectButton() {
    if (
      isCheckoutType ||
      isBookingType ||
      isMessageType ||
      !expanded ||
      !flightCheckoutEnabled
    ) {
      return null
    }
    if (directToAirline) {
      const airlineUrl =
        currentSlice.flight_legs[0]?.properties.airlines[0]?.website_url
      if (!airlineUrl) return null
      return (
        <Button
          variant="primary"
          size="sm"
          className="cursor-pointer self-start @lg:mr-3"
          href={
            airlineUrl.startsWith("http") ? airlineUrl : `https://${airlineUrl}`
          }
          target="_blank"
          onClick={(e) => {
            e.stopPropagation()
            trackFlightSelect()
          }}
          iconAfter={IconGoTo}
          rel="noopener noreferrer"
        >
          Book
        </Button>
      )
    }
    return (
      <Button
        as="span"
        variant="primary"
        size="sm"
        className="cursor-pointer self-start @lg:mr-3"
        onClick={(e) => {
          e.stopPropagation()
          handleSelect()
        }}
      >
        <span>{isDetailsType ? "Select" : "Change"}</span>
        <span className="hidden @sm:inline"> flight</span>
      </Button>
    )
  }

  function trackFlightSelect() {
    sendAnalyticsEvent({
      name: isDetailsType ? "FlightSelect" : "FlightEdit",
      properties: flightOptionToAmplitudeProperties(flightOption),
    })
  }

  function handleSelect() {
    if (onSelect && flightCheckoutEnabled) {
      onSelect(flightOption)
      dispatch(setHoveredFlight(undefined))
      trackFlightSelect()
    }
  }

  return (
    <>
      <Collapsible
        open={expanded}
        onOpenChange={setExpanded}
        className={cn(
          "group relative mt-4 mb-2 rounded-2xl bg-background text-sm leading-tight transition-colors hover:border-gray-7 data-open:border-gray-7",
          !isMessageType && "border",
          className
        )}
        {...hoverProps}
      >
        {header}

        {/*TODO set cursor-default only when message type*/}
        <AriaButton
          as="div"
          className={cn(
            "cursor-default px-4 py-5",
            isDetailsType && "pb-3.5",
            isMessageType && "pb-0"
          )}
          onClick={
            isDetailsType ? () => handleSelect() : () => setExpanded(!expanded)
          }
        >
          {isDetailsType && (
            <FlightOptionBadges
              badges={flightOptionToBadges(flightOption)}
              favorite={flightOption.favorite}
              className="absolute -top-2"
            />
          )}

          <div className="@container">
            <div className="flex">
              <FlightSliceCardHeader
                flightSlice={currentSlice}
                minimal={expanded}
                isReturn={isReturn}
                sliceLabel={sliceLabel}
                sourceType={flightOption.source_type}
              />

              {selectButton()}

              {!isCheckoutType && !isBookingType && !isMessageType && (
                <div className="no-translate w-[5.75em] shrink-0 text-right">
                  {isDetailsType && <FlightOptionPrice option={flightOption} />}
                </div>
              )}
              <div className="-mr-1 ml-1.5 @xl:ml-3">
                {alwaysExpanded ? null : (
                  <CollapsibleTrigger
                    render={
                      <IconButton
                        variant="ghost"
                        icon={{
                          icon: IconChevronDown,
                          className: cn(
                            "transition-transform",
                            expanded && "rotate-180"
                          ),
                        }}
                        size="sm"
                        label="Show details"
                      />
                    }
                    onClick={(e) => {
                      e.stopPropagation()
                      sendAnalyticsEvent({
                        name: expanded ? "FlightCardClose" : "FlightCardOpen",
                        properties:
                          flightOptionToAmplitudeProperties(flightOption),
                      })
                    }}
                  />
                )}
              </div>
            </div>
            {isMessageType ? (
              <div className="mt-2">{cancellationPolicy()}</div>
            ) : null}
          </div>

          {hasCancellationPolicy && <Separator className="my-2.5" />}
          {!isBookingType && !isMessageType && cancellationPolicy()}
        </AriaButton>

        <CollapsibleContent className="text-xs">
          <div className={cn("pb-6", isDetailsType ? "pt-4" : "pt-2.5")}>
            <FlightLegsDetail
              legs={currentSlice.flight_legs}
              layovers={currentSlice.layovers}
              leg_ticketing={sliceTicketing?.leg_ticketing}
            />

            {(notes().length || 0) > 0 && (
              <>
                <Separator className="my-2.5" />
                <div className="mx-5 mt-6 flex flex-row rounded-sm border border-yellow-6 bg-yellow-2 p-2">
                  <div className="mr-4 flex items-center font-semibold">
                    <IconInfo className="text-[1.5em] text-yellow-8" />
                  </div>

                  <div className="flex-1">
                    <ul className="no-translate list-inside list-disc">
                      {notes().map((note, ix) => {
                        return <li key={`note-${ix}`}>{note.value}</li>
                      })}
                    </ul>
                  </div>
                </div>
              </>
            )}
          </div>
        </CollapsibleContent>
        {footer}
      </Collapsible>
    </>
  )
}
